import React, { useEffect, useState, useContext } from "react";
import { getSubjects } from "../../api/classSubjectApi";
import { AuthContext } from "../../store/AuthContext";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Container,
  CircularProgress,
  Alert,
  Button,
  useTheme,
  IconButton
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import StudentLayout from "../../layouts/StudentLayout";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

export default function StudentSelectSubjectSSO() {
  const { user, token } = useContext(AuthContext);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const theme = useTheme();

  const classId = params.get("class_id") || user.class_id;

  // Load Subjects
  useEffect(() => {
    loadSubjects();
  }, [classId]);

  const loadSubjects = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await getSubjects(token);
      setSubjects(res.data);
    } catch (err) {
      console.error(err);
      setError("Failed to load subjects. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const openVideos = (subjectId) => {
    navigate(`/student/select-topic-sso?class_id=${classId}&subject_id=${subjectId}`);
  };

  // Navigate to classes page by replacing route
  const goToClasses = () => {
    navigate("/student/select-class-sso", { replace: true });
  };

  // LOADING UI
  if (loading) {
    return (
      <StudentLayout>
        <Box
          sx={{
            background: "#000",
            minHeight: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column"
          }}
        >
          <CircularProgress size={50} />
          <Typography mt={2} color="#ccc">
            Loading Subjects...
          </Typography>
        </Box>
      </StudentLayout>
    );
  }

  // ERROR UI
  if (error) {
    return (
      <StudentLayout>
        <Container
          maxWidth="md"
          sx={{ py: 4, background: "#000", minHeight: "100vh" }}
        >
          <Alert
            severity="error"
            action={<Button onClick={loadSubjects}>Retry</Button>}
          >
            {error}
          </Alert>
        </Container>
      </StudentLayout>
    );
  }

  return (
      <Box sx={{ background: "#000", minHeight: "100vh", py: 4 }}>
        <Container maxWidth="md">
          {/* HEADER WITH BACK BUTTON */}
          <Box sx={{ textAlign: "center", mb: 4, position: "relative" }}>
            {/* Classes Button - Top Left */}
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={goToClasses}
              variant="outlined"
              sx={{
                display: { xs: "none", sm: "none", md: "flex", lg: "flex" }, 
                position: "absolute",
                left: 0,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#4f8ef7",
                borderColor: "#4f8ef7",
                "&:hover": {
                  borderColor: "#d24df2",
                  backgroundColor: "rgba(79, 142, 247, 0.1)"
                }
              }}
            >
              Classes
            </Button>

            <Typography
              variant="h4"
              sx={{
                fontWeight: "bold",
                mb: 1,
                background: "linear-gradient(45deg, #4f8ef7, #d24df2)",
                WebkitBackgroundClip: "text",
                color: "transparent"
              }}
            >
              Select Your Subject
            </Typography>

            <Typography variant="body1" color="#bbb">
              Choose a subject to view available video lessons.
            </Typography>
          </Box>

          {/* SUBJECT GRID */}
          <Grid container spacing={2} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            {subjects.map((sub) => (
              <Grid item xs={12} sm={6} md={4} key={sub.id}>
                <Card
                  onClick={() => openVideos(sub.id)}
                  sx={{
                    cursor: "pointer",
                    height: 220,
                    width: 200,
                    background: "#111",
                    border: "1px solid #222",
                    borderRadius: "10px",
                    transition: "0.2s",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    "&:hover": {
                      transform: "scale(1.03)",
                      boxShadow: "0 0 12px rgba(255, 255, 255, 1)"
                    }
                  }}
                >
                  <CardContent
                    sx={{
                      textAlign: "center"
                    }}
                  >
                    {/* Subject Icon Circle */}
                    <Box
                      sx={{
                        width: 70,
                        height: 70,
                        borderRadius: "50%",
                        mb: 2,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        color: "#fff",
                        fontSize: "1.7rem",
                        fontWeight: "bold"
                      }}
                    >
                      {sub.subject?.charAt(0)}
                    </Box>

                    {/* Subject Title */}
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      color="#fff"
                    >
                      {sub.subject}
                    </Typography>

                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Additional Classes Button - Bottom Center (alternative placement) */}
          <Box sx={{ textAlign: "center", mt: 4 }}>
            <Button
              variant="contained"
              onClick={goToClasses}
              sx={{
                background: "linear-gradient(45deg, #4f8ef7, #d24df2)",
                color: "#fff",
                padding: "10px 30px",
                fontWeight: "bold",
                fontSize: "1rem",
                "&:hover": {
                  background: "linear-gradient(45deg, #3a7cf0, #c13ce5)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 5px 15px rgba(79, 142, 247, 0.4)"
                }
              }}
            >
              Back to Classes
            </Button>
          </Box>

          {/* EMPTY STATE */}
          {subjects.length === 0 && (
            <Box textAlign="center" py={6}>
              <Typography variant="h6" color="#ccc">
                No subjects found
              </Typography>
              <Typography variant="body2" color="#777">
                Please try again later.
              </Typography>
            </Box>
          )}
        </Container>
      </Box>
  );
}